import { styled } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import { isNil } from 'lodash'
import React from 'react'

import { DeteriorateIcon, ImproveIcon } from '#/components/Icon/Collection'
import { OutdatedData } from '#/components/OutdatedData'
import Colors from '#/styles/Colors'
import Fonts from '#/styles/Fonts'

type FeedbackLevel = keyof typeof Colors.feedback

type Direction = 'up' | 'down'

const directionIcons = {
  down: DeteriorateIcon,
  up: ImproveIcon,
} as const

const defaultDirectionColors = {
  down: Colors.variation.deteriorate.color,
  up: Colors.variation.improve.color,
} as const

const resolveLevelColor = (level?: FeedbackLevel | null) =>
  level ? Colors.feedback[level]?.color : undefined

/**
 * Converte o `value` em número quando for `number` ou string numérica;
 * `reverse` inverte o sinal (para métricas onde queda é melhora).
 */
const resolveSignedNumber = (value: React.ReactNode, reverse?: boolean): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null
  }

  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) {
    return null
  }

  return reverse ? -numericValue : numericValue
}

/** Cor automática por sinal: positivo → melhora (verde), negativo → piora (vermelho). */
const resolveSignColor = (value: React.ReactNode, reverse?: boolean) => {
  const signedNumber = resolveSignedNumber(value, reverse)

  if (signedNumber === null || signedNumber === 0) {
    return undefined
  }

  return signedNumber > 0 ? Colors.variation.improve.color : Colors.variation.deteriorate.color
}

const Wrapper = styled('div')({
  alignItems: 'center',
  display: 'flex',
  fontSize: 14,
  fontWeight: Fonts.fontWeight.medium,
  gap: 5,
  lineHeight: 'normal',
})

interface SegmentProps {
  bold?: boolean
  textColor?: string
}

const Segment = styled('span', {
  shouldForwardProp: propName => !['bold', 'textColor'].includes(propName as string),
})<SegmentProps>(({ bold, textColor }) => ({
  color: textColor,
  fontWeight: bold ? Fonts.fontWeight.bold : undefined,
}))

export interface TypeVariationProps {
  /** Valor em negrito. */
  bold?: boolean
  /** Estilo inline aplicado ao container. */
  customStyle?: React.CSSProperties
  /** Direção do ícone de variação: `up` (melhora) ou `down` (piora);
   * o ícone só é exibido quando informado explicitamente. */
  direction?: Direction | null
  /** Nível semântico (chave de `Colors.feedback`) que define a cor do ícone;
   * quando omitido, usa as cores padrão de variação (verde/vermelho). */
  directionLevel?: FeedbackLevel | null
  /** Nível semântico (chave de `Colors.feedback`) que define a cor do valor;
   * quando omitido e o `value` for numérico, a cor é derivada do sinal. */
  level?: FeedbackLevel | null
  /** Inverte o sinal do `value` para fins de cor automática
   * (métricas onde queda é melhora). */
  reverse?: boolean
  /** Conteúdo exibido depois do valor. */
  suffix?: React.ReactNode
  /** Nível semântico (chave de `Colors.feedback`) que define a cor do sufixo. */
  suffixLevel?: FeedbackLevel | null
  /** Quando informado, envolve o conteúdo em `OutdatedData`
   * (asterisco/tooltip de dado desatualizado). */
  timestamp?: string
  /** Texto do tooltip; ignorado quando `timestamp` é informado. */
  tooltip?: React.ReactNode
  /** Valor a ser exibido. */
  value?: React.ReactNode
}

const TypeVariation = ({
  bold,
  customStyle = {},
  direction,
  directionLevel,
  level,
  reverse,
  suffix,
  suffixLevel,
  timestamp,
  tooltip,
  value,
}: TypeVariationProps) => {
  const DirectionIcon = direction ? directionIcons[direction] : null

  if (isNil(value) && isNil(suffix) && !DirectionIcon) {
    return '-'
  }

  const valueColor = resolveLevelColor(level) ?? resolveSignColor(value, reverse)

  const directionColor =
    resolveLevelColor(directionLevel) ?? (direction ? defaultDirectionColors[direction] : undefined)

  const content = (
    <Wrapper style={customStyle}>
      {DirectionIcon && <DirectionIcon color={directionColor} fontSize={14} />}
      {!isNil(value) && (
        <Segment bold={bold} textColor={valueColor}>
          {value}
        </Segment>
      )}
      {!isNil(suffix) && <Segment textColor={resolveLevelColor(suffixLevel)}>{suffix}</Segment>}
    </Wrapper>
  )

  if (timestamp) {
    return (
      <OutdatedData timestamp={timestamp} style={{ color: valueColor }}>
        {content}
      </OutdatedData>
    )
  }

  return isNil(tooltip) ? content : <Tooltip title={tooltip}>{content}</Tooltip>
}

export default TypeVariation
